import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Loader2, MessageCircle, PenLine, Users } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { waLink } from "@/data/content";
import { TESTIMONIALS } from "@/components/Testimonials";
import { Nav } from "@/components/Nav";
import { Seo } from "@/components/Seo";
import { Footer } from "@/components/Footer";
import { FadeUp, SectionHeading } from "@/components/Reveal";

const PROVERB = {
  yoruba: "Ilé làbọ̀sìnmi oko.",
  interpretation: "No matter how far we journey, home is where we return.",
};

const CommunitySignup = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [marketing, setMarketing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await api.post("/community/join", { name, email, marketing_opt_in: marketing });
      setDone(res.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="border border-jawa-turquoise/40 bg-white p-10 text-center" data-testid="community-signup-success">
        <h3 className="font-serif text-2xl font-medium text-jawa-espresso">
          {done.returning ? "Welcome back — you're already in." : "Welcome to the JÀWÀ community."}
        </h3>
        <p className="mt-3 text-sm leading-relaxed text-jawa-espresso/65" data-testid="community-signup-status">
          {done.email_sent
            ? "We've emailed your welcome note — it has the community link and how gatherings are announced."
            : "You're on the list. Our welcome email could not be confirmed just now — your place is saved regardless."}
        </p>
        {done.community_link && (
          <a
            href={done.community_link}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="community-signup-whatsapp"
            className="mt-6 inline-flex items-center gap-2 bg-jawa-espresso px-6 py-3 text-xs font-semibold uppercase tracking-[0.2em] text-jawa-ivory transition-colors hover:bg-jawa-coral"
          >
            Join the WhatsApp community (optional) <ArrowUpRight className="h-4 w-4" />
          </a>
        )}
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="border border-jawa-espresso/10 bg-white p-8" data-testid="community-signup-form">
      <h3 className="font-serif text-2xl font-medium tracking-tight text-jawa-espresso">Join the community</h3>
      <p className="mt-2 text-sm text-jawa-espresso/60">
        Name and email is all we need. WhatsApp is optional — never required.
      </p>
      <div className="mt-5 space-y-3">
        <input
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Your name"
          data-testid="community-signup-name"
          className="w-full border border-jawa-espresso/20 bg-white px-4 py-3 text-sm outline-none focus:border-jawa-turquoise"
        />
        <input
          required
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email address"
          data-testid="community-signup-email"
          className="w-full border border-jawa-espresso/20 bg-white px-4 py-3 text-sm outline-none focus:border-jawa-turquoise"
        />
        <label className="flex items-start gap-3 text-xs leading-relaxed text-jawa-espresso/60">
          <input
            type="checkbox"
            checked={marketing}
            onChange={(e) => setMarketing(e.target.checked)}
            data-testid="community-signup-marketing"
            className="mt-0.5"
          />
          Also send me occasional JÀWÀ updates and offers (optional — unsubscribe any time).
        </label>
      </div>
      <button
        type="submit"
        disabled={busy}
        data-testid="community-signup-submit"
        className="mt-5 flex w-full items-center justify-center gap-2 bg-jawa-espresso px-6 py-3.5 text-xs font-semibold uppercase tracking-[0.2em] text-jawa-ivory transition-colors hover:bg-jawa-coral disabled:opacity-60"
      >
        {busy && <Loader2 className="h-4 w-4 animate-spin" />}
        Join free
      </button>
      <p className="mt-3 text-[11px] leading-relaxed text-jawa-espresso/45">
        We use your details to run the community and send your welcome email. Stored securely, never shared.
      </p>
    </form>
  );
};

const PILLARS = [
  {
    icon: Users,
    title: "Stories & spotlights",
    text: "Real journeys from people who have visited, invested in and relocated to Africa with JÀWÀ — the decisions, the surprises and what they would tell their past selves.",
  },
  {
    icon: MessageCircle,
    title: "The WhatsApp community",
    text: "Where members swap recommendations, ask the questions Google can't answer and hear about gatherings first. Free to join for members.",
  },
  {
    icon: PenLine,
    title: "Your voice in the A–Z",
    text: "The community shapes the guides: recommend a restaurant, flag a change, tell us what a new returnee needs to know. Every entry gets sharper because of it.",
  },
];

export default function CommunityPage() {
  return (
    <div className="bg-jawa-ivory" data-testid="community-page">
      <Nav />
      <Seo
        title="Community — Stories, Connections & Shared Knowledge | JÀWÀ Africa"
        description="The JÀWÀ community: real return and travel stories, member recommendations, gatherings and the WhatsApp community behind the guides."
      />
      <main className="mx-auto max-w-7xl px-6 pb-24 pt-32 lg:px-10 lg:pt-40">
        <FadeUp>
          <p className="text-xs font-semibold uppercase tracking-[0.35em] text-jawa-coral">Community</p>
          <h1 className="mt-4 max-w-3xl font-serif text-4xl font-medium tracking-tight text-jawa-espresso sm:text-5xl">
            The people who make JÀWÀ real.
          </h1>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-jawa-espresso/65">
            JÀWÀ exists because of a community — the diaspora comparing notes on WhatsApp threads, the
            returnees sharing what they wish they'd known, the travellers asking better questions. This is
            where those conversations live.
          </p>
        </FadeUp>

        <div className="mt-14 grid gap-6 md:grid-cols-3" data-testid="community-pillars">
          {PILLARS.map((p, i) => (
            <FadeUp key={p.title} delay={i * 0.06}>
              <div className="flex h-full flex-col border border-jawa-espresso/10 bg-white p-8">
                <p.icon className="h-6 w-6 text-jawa-gold" />
                <h2 className="mt-5 font-serif text-xl font-medium tracking-tight text-jawa-espresso">{p.title}</h2>
                <p className="mt-3 text-sm leading-relaxed text-jawa-espresso/65">{p.text}</p>
              </div>
            </FadeUp>
          ))}
        </div>

        <FadeUp className="mt-20">
          <div className="mx-auto max-w-3xl text-center" data-testid="community-proverb">
            <p className="text-[10px] uppercase tracking-[0.35em] text-jawa-gold">A Yoruba proverb we live by</p>
            <p className="mt-5 font-serif text-2xl italic leading-snug text-jawa-espresso sm:text-3xl">
              “{PROVERB.yoruba}”
            </p>
            <p className="mt-4 text-sm italic text-jawa-espresso/60">“{PROVERB.interpretation}”</p>
            <p className="mt-1 text-[10px] uppercase tracking-[0.2em] text-jawa-espresso/40">An interpretation of the proverb's meaning</p>
            <p className="mt-4 text-sm leading-relaxed text-jawa-espresso/65">
              Wherever life takes us, home calls us back. JÀWÀ — "come back" in Yoruba — exists to make that
              return warmer, easier and shared with people who understand it.
            </p>
          </div>
        </FadeUp>

        <div className="mx-auto mt-16 max-w-xl">
          <FadeUp>
            <CommunitySignup />
          </FadeUp>
        </div>

        <FadeUp className="mt-20">
          <SectionHeading overline="Stories from the community" title="Told by the people who lived it." />
          <p className="mt-4 max-w-xl text-sm leading-relaxed text-jawa-espresso/60">
            Shared with permission by JÀWÀ clients and members.
          </p>
        </FadeUp>
        <div className="mt-10 grid gap-6 md:grid-cols-2" data-testid="community-stories">
          {TESTIMONIALS.map((t, i) => (
            <FadeUp key={t.id} delay={i * 0.05}>
              <article className="flex h-full flex-col overflow-hidden border border-jawa-espresso/10 bg-white" data-testid={`community-story-${t.id}`}>
                <div className="relative h-56 overflow-hidden">
                  <img src={t.image} alt={t.name} loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
                  <span className="absolute left-4 top-4 bg-jawa-nearblack/70 px-3 py-1.5 text-[10px] uppercase tracking-[0.25em] text-jawa-ivory backdrop-blur-sm">
                    {t.journey} journey
                  </span>
                </div>
                <div className="flex flex-1 flex-col p-7">
                  <h3 className="font-serif text-xl font-medium tracking-tight text-jawa-espresso">{t.headline}</h3>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-jawa-espresso/65">
                    “{t.quote.length > 320 ? `${t.quote.slice(0, 320).trimEnd()}…` : t.quote}”
                  </p>
                  <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-jawa-stone pt-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-jawa-espresso/70">{t.name}</p>
                    <Link
                      to={t.journeyHref}
                      className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-jawa-coral transition-colors hover:text-jawa-espresso"
                    >
                      Their journey <ArrowUpRight className="h-3.5 w-3.5" />
                    </Link>
                  </div>
                </div>
              </article>
            </FadeUp>
          ))}
        </div>

        <FadeUp className="mt-20 grid gap-6 lg:grid-cols-2">
          <div className="flex h-full flex-col justify-center bg-jawa-espresso p-10 lg:p-14" data-testid="community-join">
            <p className="text-xs uppercase tracking-[0.3em] text-jawa-gold">Join in</p>
            <h2 className="mt-4 font-serif text-3xl font-medium tracking-tight text-jawa-ivory">
              Gatherings, recommendations and real answers — in the WhatsApp community.
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-jawa-ivory/70">
              Networking evenings, cultural gatherings and the day-to-day knowledge exchange happen in the
              JÀWÀ WhatsApp community — events are announced there first. The community is members-only:
              your invite arrives in your welcome email when your membership is activated.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link
                to="/memberships"
                data-testid="community-join-whatsapp"
                className="inline-flex items-center gap-2 bg-jawa-turquoise px-7 py-3.5 text-xs font-semibold uppercase tracking-[0.2em] text-jawa-nearblack transition-colors hover:bg-jawa-ivory"
              >
                Become a member <ArrowUpRight className="h-4 w-4" />
              </Link>
              <a
                href={waLink("Hello JÀWÀ — I'd like to know more about the community.")}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="community-ask-whatsapp"
                className="inline-flex items-center gap-2 border border-jawa-ivory/40 px-7 py-3.5 text-xs font-semibold uppercase tracking-[0.2em] text-jawa-ivory transition-colors hover:border-jawa-gold hover:text-jawa-gold"
              >
                Ask about the community
              </a>
            </div>
          </div>
          <div className="flex h-full flex-col justify-center border border-jawa-turquoise/40 bg-white p-10 lg:p-14" data-testid="community-contribute">
            <p className="text-xs uppercase tracking-[0.3em] text-jawa-coral">Contribute</p>
            <h2 className="mt-4 font-serif text-3xl font-medium tracking-tight text-jawa-espresso">
              Have a story, a discovery or a recommendation?
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-jawa-espresso/65">
              Tell us about your return, your favourite Lagos find, or the place every first-timer should
              see. The best contributions end up in the A–Z guides and the Journal — credited to you, with
              your permission.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <a
                href={waLink("Hello JÀWÀ — I'd like to share a story or recommendation for the community.")}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="community-share-whatsapp"
                className="inline-flex items-center gap-2 bg-jawa-espresso px-7 py-3.5 text-xs font-semibold uppercase tracking-[0.2em] text-jawa-ivory transition-colors hover:bg-jawa-coral"
              >
                Share via WhatsApp <ArrowUpRight className="h-4 w-4" />
              </a>
              <Link
                to="/concierge"
                data-testid="community-enquiry-link"
                className="inline-flex items-center gap-2 border border-jawa-espresso/25 px-7 py-3.5 text-xs font-semibold uppercase tracking-[0.2em] text-jawa-espresso transition-colors hover:border-jawa-turquoise hover:text-jawa-turquoise"
              >
                Send an enquiry
              </Link>
            </div>
          </div>
        </FadeUp>
      </main>
      <Footer />
    </div>
  );
}
